
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PersonStoryModal from '../../../components/feature/PersonStoryModal';
import { peopleStories } from '../../../mocks/peopleStories';

const PeopleCommunitySection = () => {
  const { i18n } = useTranslation();
  const [selectedPerson, setSelectedPerson] = useState<typeof peopleStories[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // メンバーの表示順をランダムにする関数
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // ページ読み込み時にメンバーの順序をランダム化
  const shuffledPeople = useMemo(() => shuffleArray(peopleStories), []);

  const handleReadStory = (person: typeof peopleStories[0]) => {
    setSelectedPerson(person);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedPerson(null), 300);
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6">
            {i18n.language === 'ja' ? '吉野に集まる人たち' : 'People Gathering in Yoshino'}
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {i18n.language === 'ja' 
              ? 'すごい人ばかり？いえ、みんな最初は「気になる。でも何から?」でした'
              : 'All amazing people? No, everyone started with "Curious. But where to start?"'}
          </p>
        </motion.div>

        {/* People Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
          {shuffledPeople.map((person, index) => (
            <motion.article
              key={`${person.name}-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="group"
            >
              <div className="bg-stone-50 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all">
                {/* Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={person.imageUrl}
                    alt={`${i18n.language === 'ja' ? person.name : person.nameEn}さんの写真`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {i18n.language === 'ja' ? person.name : person.nameEn}
                  </h3>
                  <p className="text-sm text-amber-700 font-medium mb-4">
                    {i18n.language === 'ja' ? person.tagline : person.taglineEn}
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                    {i18n.language === 'ja' ? person.story : person.storyEn}
                  </p>
                  <button 
                    onClick={() => handleReadStory(person)}
                    className="text-amber-700 hover:text-amber-800 font-bold text-sm flex items-center gap-2 cursor-pointer group"
                  >
                    <span className="underline underline-offset-4">
                      {i18n.language === 'ja' ? 'ストーリーを読む' : 'Read Story'}
                    </span>
                    <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center bg-stone-50 rounded-2xl p-12"
        >
          <p className="text-xl md:text-2xl text-gray-900 font-bold mb-6">
            {i18n.language === 'ja' 
              ? '「自分もこんな風に関われるかな？」'
              : '"Could I get involved like this too?"'}
          </p>
          <p className="text-base md:text-lg text-gray-700 mb-8">
            {i18n.language === 'ja' 
              ? '関われます。みんな最初は「何もできない」と思っていました。'
              : 'You can get involved. Everyone initially thought "I can\'t do anything."'}
          </p>
          <a
            href="/consultation"
            className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-bold transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            aria-label={i18n.language === 'ja' ? '吉野なんでも相談所で相談する' : 'Consult at Yoshino Consultation Desk'}
          >
            <i className="ri-chat-3-line text-xl w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
            {i18n.language === 'ja' ? 'まずは相談してみる' : 'Try Consulting First'}
          </a>
        </motion.div>
      </div>

      {/* Modal */}
      {selectedPerson && (
        <PersonStoryModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          person={selectedPerson}
          language={i18n.language}
        />
      )}
    </section>
  );
};

export default PeopleCommunitySection;
